import {
    Button,
    Card,
    Progress,
    Space,
    Tag,
    Typography,
    message,
} from 'antd'

import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloudDownloadOutlined,
    ReloadOutlined,
    ExperimentOutlined,
} from '@ant-design/icons'

import {
    useCallback,
    useEffect,
    useState,
} from 'react'

import {
    useNavigate,
    useParams,
} from 'react-router-dom'

import fetch from 'auth/FetchInterceptor'

import {
    env,
} from 'configs/EnvironmentConfig'

import {
    NetworkService,
    OfflineGameService,
    OfflineLeadService,
    OfflineSpinService,
    OfflineLobbyService,
} from 'offline'

const {
    Title,
    Text,
} = Typography

const API_BASE_URL =
    env.API_ENDPOINT_URL

const getMediaUrl = (url) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('blob:') ||
        url.startsWith('data:')
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

const STATUS = {
    WAITING: 'WAITING',
    RUNNING: 'RUNNING',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
}

const OfflinePreparePage = () => {
    const { slug } =
        useParams()

    const navigate =
        useNavigate()

    const [
        running,
        setRunning,
    ] = useState(false)

    const [
        preparedInfo,
        setPreparedInfo,
    ] = useState(null)

    const [
        contentOutdated,
        setContentOutdated,
    ] = useState(false)

    const [
        testingOffline,
        setTestingOffline,
    ] = useState(false)

    const [
        offlineTestResult,
        setOfflineTestResult,
    ] = useState(null)

    const [
        checks,
        setChecks,
    ] = useState({
        api: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando verificação',
        },

        serviceWorker: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando verificação',
        },

        game: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        memory: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        lobby: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        leadForm: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        prizes: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        media: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando preparação',
        },

        pending: {
            status:
                STATUS.WAITING,
            text:
                'Aguardando verificação',
        },
    })

    const updateCheck =
        useCallback(
            (
                key,
                status,
                text,
            ) => {
                setChecks(
                    (current) => ({
                        ...current,

                        [key]: {
                            status,
                            text,
                        },
                    }),
                )
            },
            [],
        )


    const cacheMediaFile =
        async (url) => {
            try {
                const response =
                    await window.fetch(
                        url,
                        {
                            cache:
                                'reload',
                        },
                    )

                if (
                    !response.ok &&
                    response.type !==
                    'opaque'
                ) {
                    throw new Error(
                        `HTTP_${response.status}`,
                    )
                }

                /*
                 * O Service Worker já intercepta
                 * esses recursos e salva no cache.
                 *
                 * O fetch aqui força o navegador
                 * a visitar cada mídia.
                 */

                return true
            } catch (error) {
                console.warn(
                    '[OFFLINE PREPARE] Falha na mídia:',
                    url,
                    error,
                )

                return false
            }
        }

    const loadPreviousPreparation =
        useCallback(
            async () => {
                try {
                    const storedGame =
                        await OfflineGameService
                            .getGameBySlug(
                                slug,
                            )

                    if (!storedGame?.id) {
                        return
                    }

                    const info =
                        await OfflineGameService
                            .getMeta(
                                `game:${storedGame.id}:offline-prepared-info`,
                            )

                    if (info) {
                        setPreparedInfo(
                            info,
                        )
                    }
                } catch (error) {
                    console.warn(
                        '[OFFLINE PREPARE] Não foi possível carregar preparação anterior:',
                        error,
                    )
                }
            },
            [slug],
        )

    const checkPreparationStatus =
        useCallback(
            async () => {
                try {
                    const storedGame =
                        await OfflineGameService
                            .getGameBySlug(
                                slug,
                            )

                    if (!storedGame?.id) {
                        setContentOutdated(false)
                        return
                    }

                    const info =
                        await OfflineGameService
                            .getMeta(
                                `game:${storedGame.id}:offline-prepared-info`,
                            )

                    if (info) {
                        setPreparedInfo(
                            info,
                        )
                    }

                    const apiOnline =
                        await NetworkService
                            .checkApiConnection()

                    if (!apiOnline) {
                        return
                    }

                    const currentGame =
                        await fetch({
                            url:
                                `/games/slug/${slug}`,

                            method:
                                'get',

                            silentNetworkError:
                                true,
                        })

                    if (
                        !info?.gameUpdatedAt ||
                        !currentGame?.updatedAt
                    ) {
                        setContentOutdated(false)
                        return
                    }

                    const preparedVersion =
                        new Date(
                            info.gameUpdatedAt,
                        ).getTime()

                    const currentVersion =
                        new Date(
                            currentGame.updatedAt,
                        ).getTime()

                    setContentOutdated(
                        preparedVersion !==
                        currentVersion,
                    )
                } catch (error) {
                    console.warn(
                        '[OFFLINE PREPARE] Não foi possível verificar versão da preparação:',
                        error,
                    )
                }
            },
            [slug],
        )

    const testOfflineReadiness =
        useCallback(
            async () => {
                if (!slug) {
                    message.error(
                        'Slug do jogo não informado.',
                    )

                    return
                }

                setTestingOffline(true)
                setOfflineTestResult(null)

                try {
                    const result = {
                        game:
                            false,

                        memory:
                            false,

                        lobby:
                            false,

                        lobbyRequired:
                            false,

                        leadForm:
                            false,

                        leadFormRequired:
                            false,

                        prizes:
                            false,

                        serviceWorker:
                            false,

                        appCache:
                            false,

                        media:
                            false,

                        missingMedia:
                            [],
                    }

                    /*
                     * =====================================
                     * 1. JOGO NO INDEXEDDB
                     * =====================================
                     */

                    const storedGame =
                        await OfflineGameService
                            .getGameBySlug(
                                slug,
                            )

                    if (!storedGame?.id) {
                        throw new Error(
                            'OFFLINE_GAME_NOT_FOUND',
                        )
                    }

                    result.game =
                        true

                    /*
                     * =====================================
                     * 1.1 JOGO DA MEMÓRIA
                     * =====================================
                     */

                    let storedMemory =
                        null

                    if (
                        storedGame.type ===
                        'MEMORY'
                    ) {
                        storedMemory =
                            await OfflineGameService
                                .getMemoryConfig(
                                    storedGame.id,
                                )

                        result.memory =
                            Boolean(
                                storedMemory,
                            )
                    } else {
                        result.memory =
                            true
                    }

                    /*
  * =====================================
  * 1.2 LOBBY OFFLINE
  * =====================================
  */

                    const preparationInfo =
                        await OfflineGameService
                            .getMeta(
                                `game:${storedGame.id}:offline-prepared-info`,
                            )

                    let storedLobby =
                        null

                    const lobbyRequired =
                        preparationInfo?.lobbyPrepared ===
                        true &&
                        Boolean(
                            preparationInfo?.lobbyEventSlug,
                        )

                    result.lobbyRequired =
                        lobbyRequired

                    if (
                        lobbyRequired
                    ) {
                        storedLobby =
                            await OfflineLobbyService
                                .getLobby(
                                    preparationInfo.lobbyEventSlug,
                                )

                        result.lobby =
                            Boolean(
                                storedLobby?.lobby,
                            )
                    } else {
                        // Este cliente/jogo não utiliza lobby.
                        result.lobby =
                            true
                    }

                    /*
                     * =====================================
                     * 2. FORMULÁRIO
                     * =====================================
                     */

                    const storedLeadForm =
                        await OfflineGameService
                            .getLeadForm(
                                storedGame.id,
                            )

                    const leadFormRequired =
                        preparationInfo
                            ?.leadFormPrepared ===
                        true

                    result.leadFormRequired =
                        leadFormRequired

                    result.leadForm =
                        !leadFormRequired ||
                        Boolean(
                            storedLeadForm,
                        )


                    /*
                     * =====================================
                     * 3. PRÊMIOS
                     * =====================================
                     */

                    const storedPrizes =
                        await OfflineGameService
                            .getPrizes(
                                storedGame.id,
                            )

                    if (
                        storedGame.type ===
                        'ROULETTE'
                    ) {
                        result.prizes =
                            Array.isArray(
                                storedPrizes,
                            ) &&
                            storedPrizes.length > 0
                    } else {
                        /*
                         * Memória e outros jogos
                         * não precisam obrigatoriamente
                         * de prêmios.
                         */
                        result.prizes =
                            true
                    }

                    /*
                     * =====================================
                     * 4. SERVICE WORKER
                     * =====================================
                     */

                    if (
                        'serviceWorker'
                        in navigator
                    ) {
                        const registration =
                            await navigator
                                .serviceWorker
                                .ready

                        result.serviceWorker =
                            Boolean(
                                registration &&
                                navigator
                                    .serviceWorker
                                    .controller,
                            )
                    }

                    /*
                     * =====================================
                     * 5. CACHE DA APLICAÇÃO
                     * =====================================
                     */

                    const cachedIndex =
                        await caches.match(
                            '/index.html',
                        )

                    const cachedRoot =
                        await caches.match(
                            '/',
                        )

                    result.appCache =
                        Boolean(
                            cachedIndex ||
                            cachedRoot,
                        )

                    /*
                     * =====================================
                     * 6. MÍDIAS
                     * =====================================
                     */

                    const prizes =
                        Array.isArray(
                            storedGame.prizes,
                        )
                            ? storedGame.prizes
                            : storedPrizes || []

                    /*
                     * -------------------------------------
                     * LOBBY
                     * -------------------------------------
                     */

                    const lobbyCards =
                        Array.isArray(
                            storedLobby?.lobby?.cards,
                        )
                            ? storedLobby.lobby.cards
                            : []

                    /*
                     * -------------------------------------
                     * MEMÓRIA
                     * -------------------------------------
                     */

                    const storedMemoryConfig =
                        storedMemory?.config ||
                        null

                    const storedMemoryCards =
                        Array.isArray(
                            storedMemory?.cards,
                        )
                            ? storedMemory.cards
                            : []

                    /*
                     * =====================================
                     * LISTA COMPLETA DE MÍDIAS
                     * =====================================
                     */

                    const media = [
                        /*
                         * JOGO GERAL
                         */

                        storedGame.logoUrl,
                        storedGame.backgroundUrl,

                        storedGame.formPageBackgroundUrl,
                        storedGame.formPageLogoUrl,

                        storedGame.prizePageBackgroundUrl,
                        storedGame.prizePageLogoUrl,
                        storedGame.prizePageFrameUrl,

                        storedGame.idleScreenBackgroundUrl,
                        storedGame.idleScreenVideoUrl,

                        storedGame.gameplayBackgroundUrl,
                        storedGame.gameplayLogoUrl,

                        /*
                         * LOBBY
                         */

                        storedLobby?.lobby?.backgroundUrl,
                        storedLobby?.lobby?.logoUrl,

                        ...lobbyCards.map(
                            (
                                card,
                            ) =>
                                card?.imageUrl,
                        ),

                        /*
                         * JOGO DA MEMÓRIA
                         */

                        storedMemoryConfig
                            ?.startScreenBackgroundUrl,

                        storedMemoryConfig
                            ?.startScreenLogoUrl,

                        storedMemoryConfig
                            ?.resultBackgroundUrl,

                        storedMemoryConfig
                            ?.resultLogoUrl,

                        storedMemoryConfig
                            ?.resultCompletedImageUrl,

                        storedMemoryConfig
                            ?.resultTimeoutImageUrl,

                        storedMemoryConfig
                            ?.cardBackImageUrl,

                        /*
                         * SONS DA MEMÓRIA
                         */

                        storedMemoryConfig
                            ?.flipSoundUrl,

                        storedMemoryConfig
                            ?.matchSoundUrl,

                        storedMemoryConfig
                            ?.errorSoundUrl,

                        storedMemoryConfig
                            ?.countdownSoundUrl,

                        storedMemoryConfig
                            ?.goSoundUrl,

                        storedMemoryConfig
                            ?.completeSoundUrl,

                        storedMemoryConfig
                            ?.musicUrl,

                        /*
                         * CARTAS DA MEMÓRIA
                         */

                        ...storedMemoryCards
                            .flatMap(
                                (
                                    card,
                                ) => [
                                        card?.imageAUrl,
                                        card?.imageBUrl,
                                    ],
                            ),

                        /*
                         * PRÊMIOS
                         */

                        ...prizes.map(
                            (
                                prize,
                            ) =>
                                prize?.imageUrl,
                        ),
                    ]
                        .filter(Boolean)
                        .map(
                            getMediaUrl,
                        )

                    /*
                     * Remove URLs repetidas.
                     */

                    const uniqueMedia = [
                        ...new Set(
                            media,
                        ),
                    ]

                    const missingMedia =
                        []

                    /*
                     * =====================================
                     * VERIFICAR CACHE DAS MÍDIAS
                     * =====================================
                     */

                    for (
                        const mediaUrl
                        of uniqueMedia
                    ) {
                        const cached =
                            await caches.match(
                                mediaUrl,
                            )

                        if (!cached) {
                            missingMedia.push(
                                mediaUrl,
                            )
                        }
                    }

                    result.missingMedia =
                        missingMedia

                    result.media =
                        missingMedia.length ===
                        0

                    /*
                     * =====================================
                     * RESULTADO FINAL
                     * =====================================
                     */

                    const approved =
                        result.game &&
                        result.memory &&
                        result.lobby &&
                        result.leadForm &&
                        result.prizes &&
                        result.serviceWorker &&
                        result.appCache &&
                        result.media

                    const finalResult = {
                        ...result,

                        approved,

                        testedAt:
                            new Date()
                                .toISOString(),
                    }

                    setOfflineTestResult(
                        finalResult,
                    )

                    if (approved) {
                        message.success(
                            'Teste offline aprovado.',
                        )
                    } else {
                        message.warning(
                            'O dispositivo ainda possui itens pendentes para uso offline.',
                        )
                    }
                } catch (error) {
                    console.error(
                        '[OFFLINE TEST] Erro:',
                        error,
                    )

                    setOfflineTestResult({
                        approved:
                            false,

                        error:
                            error?.message ||
                            'UNKNOWN_ERROR',
                    })

                    message.error(
                        'Não foi possível concluir o teste offline.',
                    )
                } finally {
                    setTestingOffline(
                        false,
                    )
                }
            },
            [
                slug,
            ],
        )

    const prepare =
        useCallback(
            async () => {
                if (!slug) {
                    message.error(
                        'Slug do jogo não informado.',
                    )

                    return
                }

                setRunning(true)

                try {
                    /*
                     * =====================================
                     * 1. API REAL
                     * =====================================
                     */

                    updateCheck(
                        'api',
                        STATUS.RUNNING,
                        'Verificando API...',
                    )

                    const apiOnline =
                        await NetworkService
                            .checkApiConnection()

                    if (!apiOnline) {
                        updateCheck(
                            'api',
                            STATUS.ERROR,
                            'API indisponível. Conecte o dispositivo à internet para preparar o modo offline.',
                        )

                        message.error(
                            'É necessário estar online para preparar o dispositivo.',
                        )

                        return
                    }

                    updateCheck(
                        'api',
                        STATUS.SUCCESS,
                        'API disponível',
                    )

                    /*
                     * =====================================
                     * 2. SERVICE WORKER
                     * =====================================
                     */

                    updateCheck(
                        'serviceWorker',
                        STATUS.RUNNING,
                        'Verificando Service Worker...',
                    )

                    if (
                        !(
                            'serviceWorker'
                            in navigator
                        )
                    ) {
                        updateCheck(
                            'serviceWorker',
                            STATUS.ERROR,
                            'Service Worker não suportado',
                        )

                        return
                    }

                    const registration =
                        await navigator
                            .serviceWorker
                            .ready

                    if (
                        !registration
                    ) {
                        updateCheck(
                            'serviceWorker',
                            STATUS.ERROR,
                            'Service Worker não está ativo',
                        )

                        return
                    }

                    updateCheck(
                        'serviceWorker',
                        STATUS.SUCCESS,
                        'Service Worker ativo',
                    )

                    /*
                     * =====================================
                     * 3. JOGO
                     * =====================================
                     */

                    updateCheck(
                        'game',
                        STATUS.RUNNING,
                        'Baixando configuração atual...',
                    )

                    const gameData =
                        await fetch({
                            url:
                                `/games/slug/${slug}`,

                            method:
                                'get',

                            silentNetworkError:
                                true,
                        })

                    if (
                        !gameData?.id
                    ) {
                        throw new Error(
                            'GAME_NOT_FOUND',
                        )
                    }

                    await OfflineGameService
                        .saveGame(
                            gameData,
                        )


                    updateCheck(
                        'game',
                        STATUS.SUCCESS,
                        `Jogo preparado: ${gameData.name || gameData.title || slug}`,
                    )

                    /*
 * =====================================
 * 3.1 LOBBY DO EVENTO
 * =====================================
 */

                    updateCheck(
                        'lobby',
                        STATUS.RUNNING,
                        'Preparando página inicial dos jogos...',
                    )

                    let lobbyData =
                        null

                    try {
                        if (
                            gameData.eventId
                        ) {
                            lobbyData =
                                await fetch({
                                    url:
                                        `/game-lobby/public/event-id/${gameData.eventId}`,

                                    method:
                                        'get',

                                    silentNetworkError:
                                        true,
                                })
                        }

                        if (
                            lobbyData?.event?.slug &&
                            lobbyData?.lobby
                        ) {
                            await OfflineLobbyService
                                .saveLobby(
                                    lobbyData.event.slug,
                                    lobbyData,
                                )

                            updateCheck(
                                'lobby',
                                STATUS.SUCCESS,
                                `Página inicial preparada: ${lobbyData.event.name || lobbyData.event.slug}`,
                            )
                        } else {
                            updateCheck(
                                'lobby',
                                STATUS.SUCCESS,
                                'Evento não possui página inicial ativa',
                            )
                        }
                    } catch (error) {
                        console.warn(
                            '[OFFLINE PREPARE] Página inicial não disponível:',
                            error,
                        )

                        updateCheck(
                            'lobby',
                            STATUS.SUCCESS,
                            'Evento não possui página inicial ativa',
                        )
                    }

                    /*
 * =====================================
 * 3.2 JOGO DA MEMÓRIA
 * =====================================
 */

                    updateCheck(
                        'memory',
                        STATUS.RUNNING,
                        'Verificando configuração da memória...',
                    )

                    let memoryData =
                        null

                    if (
                        gameData.type ===
                        'MEMORY'
                    ) {
                        try {
                            memoryData =
                                await fetch({
                                    url:
                                        `/memory-games/${gameData.id}`,

                                    method:
                                        'get',

                                    silentNetworkError:
                                        true,
                                })

                            if (
                                !memoryData
                            ) {
                                throw new Error(
                                    'MEMORY_CONFIG_NOT_FOUND',
                                )
                            }

                            await OfflineGameService
                                .saveMemoryConfig(
                                    gameData.id,
                                    memoryData,
                                )

                            const memoryCards =
                                Array.isArray(
                                    memoryData?.cards,
                                )
                                    ? memoryData.cards
                                    : []

                            updateCheck(
                                'memory',
                                STATUS.SUCCESS,
                                `Memória preparada: ${memoryCards.length} par(es)`,
                            )
                        } catch (error) {
                            console.error(
                                '[OFFLINE PREPARE] Erro ao preparar memória:',
                                error,
                            )

                            updateCheck(
                                'memory',
                                STATUS.ERROR,
                                'Não foi possível preparar o Jogo da Memória',
                            )

                            throw error
                        }
                    } else {
                        updateCheck(
                            'memory',
                            STATUS.SUCCESS,
                            'Não se aplica a este jogo',
                        )
                    }

                    /*
                     * =====================================
                     * 4. FORMULÁRIO
                     * =====================================
                     */

                    updateCheck(
                        'leadForm',
                        STATUS.RUNNING,
                        'Preparando formulário...',
                    )

                    let leadFormData =
                        null

                    try {
                        leadFormData =
                            await fetch({
                                url:
                                    `/public/games/${gameData.id}/lead-form`,

                                method:
                                    'get',

                                silentNetworkError:
                                    true,
                            })
                    } catch (error) {
                        console.warn(
                            '[OFFLINE PREPARE] Formulário não encontrado ou indisponível:',
                            error,
                        )
                    }

                    await OfflineGameService
                        .saveLeadForm(
                            gameData.id,
                            leadFormData || null,
                        )

                    const leadFormPrepared =
                        Boolean(
                            leadFormData,
                        )

                    updateCheck(
                        'leadForm',
                        STATUS.SUCCESS,
                        leadFormPrepared
                            ? 'Formulário preparado'
                            : 'Este jogo não utiliza formulário',
                    )

                    /*
                     * =====================================
                     * 5. PRÊMIOS
                     * =====================================
                     */

                    updateCheck(
                        'prizes',
                        STATUS.RUNNING,
                        'Preparando prêmios...',
                    )

                    const prizes =
                        Array.isArray(
                            gameData.prizes,
                        )
                            ? gameData.prizes
                            : []

                    /*
                     * saveGame já salva também
                     * o snapshot de prizes.
                     */

                    updateCheck(
                        'prizes',
                        STATUS.SUCCESS,
                        `${prizes.length} prêmio${prizes.length === 1 ? '' : 's'} preparado${prizes.length === 1 ? '' : 's'}`,
                    )

                    /*
                     * =====================================
                     * 6. MÍDIAS
                     * =====================================
                     */

                    updateCheck(
                        'media',
                        STATUS.RUNNING,
                        'Preparando imagens e vídeos...',
                    )

                    const lobbyCards =
                        Array.isArray(
                            lobbyData?.lobby?.cards,
                        )
                            ? lobbyData.lobby.cards
                            : []

                    const memoryConfig =
                        memoryData?.config ||
                        null

                    const memoryCards =
                        Array.isArray(
                            memoryData?.cards,
                        )
                            ? memoryData.cards
                            : []

                    const media = [
                        /*
                         * =====================================
                         * JOGO GERAL
                         * =====================================
                         */

                        gameData.logoUrl,
                        gameData.backgroundUrl,

                        gameData.formPageBackgroundUrl,
                        gameData.formPageLogoUrl,

                        gameData.prizePageBackgroundUrl,
                        gameData.prizePageLogoUrl,
                        gameData.prizePageFrameUrl,

                        gameData.idleScreenBackgroundUrl,
                        gameData.idleScreenVideoUrl,

                        gameData.gameplayBackgroundUrl,
                        gameData.gameplayLogoUrl,

                        /*
                         * =====================================
                         * LOBBY
                         * =====================================
                         */

                        lobbyData?.lobby?.backgroundUrl,
                        lobbyData?.lobby?.logoUrl,

                        ...lobbyCards.map(
                            (card) =>
                                card?.imageUrl,
                        ),

                        /*
                        * =====================================
                        * JOGO DA MEMÓRIA
                        * =====================================
                        */

                        memoryConfig?.startScreenBackgroundUrl,
                        memoryConfig?.startScreenLogoUrl,

                        memoryConfig?.resultBackgroundUrl,
                        memoryConfig?.resultLogoUrl,

                        // NOVAS ARTES DE RESULTADO
                        memoryConfig?.resultCompletedImageUrl,
                        memoryConfig?.resultTimeoutImageUrl,

                        memoryConfig?.cardBackImageUrl,

                        /*
                         * SONS DA MEMÓRIA
                         */

                        memoryConfig?.flipSoundUrl,
                        memoryConfig?.matchSoundUrl,
                        memoryConfig?.errorSoundUrl,
                        memoryConfig?.countdownSoundUrl,
                        memoryConfig?.goSoundUrl,
                        memoryConfig?.completeSoundUrl,
                        memoryConfig?.musicUrl,

                        /*
                         * CARTAS DA MEMÓRIA
                         */

                        ...memoryCards.flatMap(
                            (card) => [
                                card?.imageAUrl,
                                card?.imageBUrl,
                            ],
                        ),

                        /*
                         * =====================================
                         * PRÊMIOS
                         * =====================================
                         */

                        ...prizes.map(
                            (prize) =>
                                prize?.imageUrl,
                        ),
                    ]
                        .filter(Boolean)
                        .map(
                            getMediaUrl,
                        )
                        .filter(Boolean)

                    const uniqueMedia = [
                        ...new Set(
                            media,
                        ),
                    ]

                    let mediaSuccess =
                        0

                    let mediaError =
                        0

                    for (
                        const mediaUrl
                        of uniqueMedia
                    ) {
                        const success =
                            await cacheMediaFile(
                                mediaUrl,
                            )

                        if (success) {
                            mediaSuccess += 1
                        } else {
                            mediaError += 1
                        }
                    }

                    if (
                        mediaError > 0
                    ) {
                        updateCheck(
                            'media',
                            STATUS.ERROR,
                            `${mediaSuccess} mídia(s) preparada(s), ${mediaError} com erro`,
                        )
                    } else {
                        updateCheck(
                            'media',
                            STATUS.SUCCESS,
                            `${mediaSuccess} mídia(s) preparada(s)`,
                        )
                    }

                    /*
                     * =====================================
                     * 7. PENDÊNCIAS LOCAIS
                     * =====================================
                     */

                    updateCheck(
                        'pending',
                        STATUS.RUNNING,
                        'Verificando registros locais...',
                    )

                    const pendingLeads =
                        await OfflineLeadService
                            .getPendingLeads()

                    const pendingSpins =
                        await OfflineSpinService
                            .getPendingSpins()

                    const totalPending =
                        (pendingLeads?.length || 0) +
                        (pendingSpins?.length || 0)

                    updateCheck(
                        'pending',
                        STATUS.SUCCESS,
                        totalPending > 0
                            ? `${totalPending} registro(s) aguardando sincronização`
                            : 'Nenhuma pendência local',
                    )

                    /*
                    * =====================================
                    * 8. INFORMAÇÕES DA PREPARAÇÃO
                    * =====================================
                    */

                    const preparedAt =
                        new Date()
                            .toISOString()

                    const preparationInfo = {
                        gameId:
                            gameData.id,

                        slug:
                            gameData.slug,

                        name:
                            gameData.name ||
                            gameData.title ||
                            slug,

                        preparedAt,

                        /*
                         * Versão da configuração
                         */
                        gameUpdatedAt:
                            gameData.updatedAt ||
                            null,

                        /*
                         * Prêmios
                         */
                        prizesCount:
                            prizes.length,

                        /*
                        * Formulário
                        */

                        leadFormPrepared:
                            Boolean(
                                leadFormData,
                            ),

                        leadFormRequired:
                            Boolean(
                                leadFormData,
                            ),

                        /*
                         * Jogo da Memória
                         */
                        memoryPrepared:
                            gameData.type ===
                                'MEMORY'
                                ? Boolean(
                                    memoryData,
                                )
                                : true,

                        memoryCardsCount:
                            Array.isArray(
                                memoryData?.cards,
                            )
                                ? memoryData.cards.length
                                : 0,

                        /*
                         * Lobby
                         */
                        lobbyPrepared:
                            Boolean(
                                lobbyData?.lobby,
                            ),

                        lobbyEventSlug:
                            lobbyData?.event?.slug ||
                            null,

                        lobbyEventId:
                            lobbyData?.event?.id ||
                            null,

                        lobbyCardsCount:
                            Array.isArray(
                                lobbyData?.lobby?.cards,
                            )
                                ? lobbyData.lobby.cards.length
                                : 0,

                        /*
                         * Mídias
                         */
                        mediaCount:
                            uniqueMedia.length,

                        mediaSuccess,

                        mediaError,

                        /*
                         * Preparação pronta
                         */
                        ready:
                            mediaError === 0,
                    }

                    /*
                     * Mantemos a chave antiga caso alguma outra
                     * parte do sistema venha a utilizá-la.
                     */
                    await OfflineGameService
                        .saveMeta(
                            `game:${gameData.id}:offline-prepared-at`,
                            preparedAt,
                        )

                    /*
                     * Informações completas da preparação.
                     */
                    await OfflineGameService
                        .saveMeta(
                            `game:${gameData.id}:offline-prepared-info`,
                            preparationInfo,
                        )

                    setPreparedInfo(
                        preparationInfo,
                    )

                    setContentOutdated(false)

                    console.log(
                        '[OFFLINE PREPARE] Preparação registrada:',
                        preparationInfo,
                    )

                    message.success(
                        mediaError > 0
                            ? 'Preparação concluída com alertas de mídia.'
                            : 'Dispositivo preparado para uso offline.',
                    )
                } catch (error) {
                    console.error(
                        '[OFFLINE PREPARE] Erro:',
                        error,
                    )

                    message.error(
                        'Não foi possível concluir a preparação offline.',
                    )
                } finally {
                    setRunning(false)
                }
            },
            [
                slug,
                updateCheck,
            ],
        )

    useEffect(() => {
        loadPreviousPreparation()
    }, [
        loadPreviousPreparation,
    ])

    useEffect(() => {
        checkPreparationStatus()
    }, [
        checkPreparationStatus,
    ])

    const formatDateTime = (
        value,
    ) => {
        if (!value) {
            return '—'
        }

        try {
            return new Date(
                value,
            ).toLocaleString(
                'pt-BR',
                {
                    day:
                        '2-digit',

                    month:
                        '2-digit',

                    year:
                        'numeric',

                    hour:
                        '2-digit',

                    minute:
                        '2-digit',

                    second:
                        '2-digit',
                },
            )
        } catch (error) {
            return '—'
        }
    }

    const checkItems = [
        {
            key: 'api',
            label:
                'Conexão com API',
        },

        {
            key:
                'serviceWorker',
            label:
                'Aplicação offline',
        },

        {
            key:
                'game',
            label:
                'Configuração do jogo',
        },

        {
            key:
                'memory',
            label:
                'Jogo da Memória',
        },

        {
            key:
                'lobby',
            label:
                'Página inicial dos jogos',
        },

        {
            key:
                'leadForm',
            label:
                'Formulário',
        },

        {
            key:
                'prizes',
            label:
                'Prêmios',
        },

        {
            key:
                'media',
            label:
                'Imagens e vídeos',
        },

        {
            key:
                'pending',
            label:
                'Pendências locais',
        },
    ]

    const successCount =
        checkItems.filter(
            (item) =>
                checks[item.key]
                    ?.status ===
                STATUS.SUCCESS,
        ).length

    const errorCount =
        checkItems.filter(
            (item) =>
                checks[item.key]
                    ?.status ===
                STATUS.ERROR,
        ).length

    const progress =
        Math.round(
            (
                successCount /
                checkItems.length
            ) * 100,
        )

    const ready =
        !running &&
        !contentOutdated &&
        errorCount === 0 &&
        successCount ===
        checkItems.length

    const getTag = (
        status,
    ) => {
        if (
            status ===
            STATUS.SUCCESS
        ) {
            return (
                <Tag
                    color="success"
                    icon={
                        <CheckCircleOutlined />
                    }
                >
                    OK
                </Tag>
            )
        }

        if (
            status ===
            STATUS.ERROR
        ) {
            return (
                <Tag
                    color="error"
                    icon={
                        <CloseCircleOutlined />
                    }
                >
                    ERRO
                </Tag>
            )
        }

        if (
            status ===
            STATUS.RUNNING
        ) {
            return (
                <Tag color="processing">
                    VERIFICANDO
                </Tag>
            )
        }

        return (
            <Tag>
                AGUARDANDO
            </Tag>
        )
    }

    return (
        <div
            style={{
                minHeight:
                    '100vh',

                background:
                    '#f3f4f6',

                padding:
                    32,

                boxSizing:
                    'border-box',
            }}
        >
            <div
                style={{
                    maxWidth:
                        820,

                    margin:
                        '0 auto',
                }}
            >
                <Card>
                    <Space
                        direction="vertical"
                        size={24}
                        style={{
                            width:
                                '100%',
                        }}
                    >
                        <div>
                            <Title
                                level={2}
                                style={{
                                    marginBottom:
                                        6,
                                }}
                            >
                                Preparar dispositivo offline
                            </Title>

                            <Text type="secondary">
                                Baixe e valide todo o conteúdo necessário antes de levar este equipamento para o evento.
                            </Text>
                        </div>

                        <Progress
                            percent={
                                progress
                            }

                            status={
                                errorCount > 0
                                    ? 'exception'
                                    : ready
                                        ? 'success'
                                        : 'active'
                            }
                        />

                        {preparedInfo && (
                            <div
                                style={{
                                    display:
                                        'grid',

                                    gridTemplateColumns:
                                        'repeat(auto-fit, minmax(180px, 1fr))',

                                    gap:
                                        12,

                                    padding:
                                        16,

                                    borderRadius:
                                        12,

                                    background:
                                        '#f8fafc',

                                    border:
                                        '1px solid #e2e8f0',
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontSize:
                                                11,

                                            color:
                                                '#64748b',

                                            fontWeight:
                                                700,

                                            textTransform:
                                                'uppercase',
                                        }}
                                    >
                                        Jogo preparado
                                    </div>

                                    <div
                                        style={{
                                            marginTop:
                                                4,

                                            fontWeight:
                                                800,

                                            color:
                                                '#0f172a',
                                        }}
                                    >
                                        {preparedInfo.name}
                                    </div>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontSize:
                                                11,

                                            color:
                                                '#64748b',

                                            fontWeight:
                                                700,

                                            textTransform:
                                                'uppercase',
                                        }}
                                    >
                                        Última preparação
                                    </div>

                                    <div
                                        style={{
                                            marginTop:
                                                4,

                                            fontWeight:
                                                800,

                                            color:
                                                '#0f172a',
                                        }}
                                    >
                                        {formatDateTime(
                                            preparedInfo.preparedAt,
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontSize:
                                                11,

                                            color:
                                                '#64748b',

                                            fontWeight:
                                                700,

                                            textTransform:
                                                'uppercase',
                                        }}
                                    >
                                        Configuração do jogo
                                    </div>

                                    <div
                                        style={{
                                            marginTop:
                                                4,

                                            fontWeight:
                                                800,

                                            color:
                                                '#0f172a',
                                        }}
                                    >
                                        {preparedInfo.gameUpdatedAt
                                            ? formatDateTime(
                                                preparedInfo.gameUpdatedAt,
                                            )
                                            : 'Sem versão informada'}
                                    </div>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontSize:
                                                11,

                                            color:
                                                '#64748b',

                                            fontWeight:
                                                700,

                                            textTransform:
                                                'uppercase',
                                        }}
                                    >
                                        Conteúdo local
                                    </div>

                                    <div
                                        style={{
                                            marginTop:
                                                4,

                                            fontWeight:
                                                800,

                                            color:
                                                '#0f172a',
                                        }}
                                    >
                                        {preparedInfo.prizesCount || 0}{' '}
                                        prêmios •{' '}
                                        {preparedInfo.mediaSuccess || 0}{' '}
                                        mídias
                                    </div>
                                </div>
                            </div>
                        )}

                        {contentOutdated && (
                            <div
                                style={{
                                    padding:
                                        16,

                                    borderRadius:
                                        12,

                                    background:
                                        '#fff7ed',

                                    border:
                                        '1px solid #fdba74',

                                    color:
                                        '#9a3412',

                                    display:
                                        'flex',

                                    alignItems:
                                        'flex-start',

                                    gap:
                                        12,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize:
                                            22,

                                        lineHeight:
                                            1,
                                    }}
                                >
                                    ⚠️
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontWeight:
                                                900,

                                            fontSize:
                                                15,
                                        }}
                                    >
                                        Conteúdo offline desatualizado
                                    </div>

                                    <div
                                        style={{
                                            marginTop:
                                                4,

                                            fontSize:
                                                13,

                                            lineHeight:
                                                1.45,
                                        }}
                                    >
                                        As configurações deste jogo foram alteradas depois da última preparação deste dispositivo.
                                        Clique em <strong>Preparar novamente</strong> antes de usar o equipamento offline no evento.
                                    </div>
                                </div>
                            </div>
                        )}

                        {offlineTestResult && (
                            <div
                                style={{
                                    padding:
                                        18,

                                    borderRadius:
                                        12,

                                    background:
                                        offlineTestResult.approved
                                            ? '#f0fdf4'
                                            : '#fff7ed',

                                    border:
                                        offlineTestResult.approved
                                            ? '1px solid #86efac'
                                            : '1px solid #fdba74',

                                    color:
                                        offlineTestResult.approved
                                            ? '#166534'
                                            : '#9a3412',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize:
                                            16,

                                        fontWeight:
                                            900,

                                        marginBottom:
                                            10,
                                    }}
                                >
                                    {offlineTestResult.approved
                                        ? '✓ APROVADO PARA USO OFFLINE'
                                        : '⚠ TESTE OFFLINE COM PENDÊNCIAS'}
                                </div>

                                <div
                                    style={{
                                        display:
                                            'grid',

                                        gap:
                                            5,

                                        fontSize:
                                            13,
                                    }}
                                >
                                    <div>
                                        Página inicial:{' '}
                                        <strong>
                                            {offlineTestResult
                                                .lobbyRequired === false
                                                ? 'NÃO UTILIZADA'
                                                : offlineTestResult
                                                    .lobby
                                                    ? 'OK'
                                                    : 'FALHOU'}
                                        </strong>
                                    </div>
                                    <div>
                                        Formulário:{' '}
                                        <strong>
                                            {offlineTestResult
                                                .leadFormRequired ===
                                                false
                                                ? 'NÃO UTILIZADO'
                                                : offlineTestResult
                                                    .leadForm
                                                    ? 'OK'
                                                    : 'FALHOU'}
                                        </strong>
                                    </div>
                                    <div>
                                        Prêmios:{' '}
                                        <strong>
                                            {offlineTestResult.prizes
                                                ? 'OK'
                                                : 'FALHOU'}
                                        </strong>
                                    </div>

                                    <div>
                                        Service Worker:{' '}
                                        <strong>
                                            {offlineTestResult.serviceWorker
                                                ? 'OK'
                                                : 'FALHOU'}
                                        </strong>
                                    </div>

                                    <div>
                                        Aplicação em cache:{' '}
                                        <strong>
                                            {offlineTestResult.appCache
                                                ? 'OK'
                                                : 'FALHOU'}
                                        </strong>
                                    </div>

                                    <div>
                                        Mídias em cache:{' '}
                                        <strong>
                                            {offlineTestResult.media
                                                ? 'OK'
                                                : 'FALHOU'}
                                        </strong>
                                    </div>

                                    {Array.isArray(
                                        offlineTestResult.missingMedia,
                                    ) &&
                                        offlineTestResult
                                            .missingMedia
                                            .length > 0 && (
                                            <div
                                                style={{
                                                    marginTop:
                                                        8,
                                                }}
                                            >
                                                <strong>
                                                    Mídias ausentes:
                                                </strong>

                                                <div
                                                    style={{
                                                        marginTop:
                                                            4,

                                                        fontSize:
                                                            11,

                                                        wordBreak:
                                                            'break-all',
                                                    }}
                                                >
                                                    {offlineTestResult
                                                        .missingMedia
                                                        .map(
                                                            (
                                                                url,
                                                                index,
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        `${url}-${index}`
                                                                    }
                                                                >
                                                                    {url}
                                                                </div>
                                                            ),
                                                        )}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                display:
                                    'grid',

                                gap:
                                    12,
                            }}
                        >
                            {checkItems.map(
                                (item) => {
                                    const check =
                                        checks[
                                        item.key
                                        ]

                                    return (
                                        <div
                                            key={
                                                item.key
                                            }
                                            style={{
                                                display:
                                                    'flex',

                                                justifyContent:
                                                    'space-between',

                                                alignItems:
                                                    'center',

                                                gap:
                                                    16,

                                                padding:
                                                    '14px 16px',

                                                border:
                                                    '1px solid #e5e7eb',

                                                borderRadius:
                                                    10,

                                                background:
                                                    '#ffffff',
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        fontWeight:
                                                            800,
                                                    }}
                                                >
                                                    {
                                                        item.label
                                                    }
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop:
                                                            3,

                                                        color:
                                                            '#6b7280',

                                                        fontSize:
                                                            13,
                                                    }}
                                                >
                                                    {
                                                        check
                                                            ?.text
                                                    }
                                                </div>
                                            </div>

                                            {getTag(
                                                check
                                                    ?.status,
                                            )}
                                        </div>
                                    )
                                },
                            )}
                        </div>

                        {ready && (
                            <div
                                style={{
                                    padding:
                                        18,

                                    borderRadius:
                                        12,

                                    background:
                                        '#f0fdf4',

                                    border:
                                        '1px solid #86efac',

                                    color:
                                        '#166534',

                                    fontWeight:
                                        800,

                                    textAlign:
                                        'center',
                                }}
                            >
                                ✓ DISPOSITIVO PRONTO PARA USO OFFLINE
                            </div>
                        )}

                        <Button
                            type="primary"
                            size="large"

                            icon={
                                running
                                    ? <ReloadOutlined />
                                    : <CloudDownloadOutlined />
                            }

                            loading={
                                running
                            }

                            onClick={
                                prepare
                            }

                            block
                        >
                            {running
                                ? 'Preparando...'
                                : 'Preparar novamente'}
                        </Button>
                        <Button
                            size="large"

                            icon={
                                <ExperimentOutlined />
                            }

                            loading={
                                testingOffline
                            }

                            disabled={
                                running
                            }

                            onClick={
                                testOfflineReadiness
                            }

                            block
                        >
                            {testingOffline
                                ? 'Testando...'
                                : 'Testar modo offline'}
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            disabled={
                                running ||
                                testingOffline ||
                                offlineTestResult?.approved !== true
                            }
                            onClick={() => {
                                navigate(
                                    `/play/${slug}`,
                                )
                            }}
                            block
                        >
                            Abrir jogo
                        </Button>
                    </Space>
                </Card>
            </div>
        </div>
    )
}

export default OfflinePreparePage